// File: frontend/src/pages/Contacts/ContactDetailPage.jsx
// Contact detail page with 360-degree view

import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  StarIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  PhoneArrowUpRightIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { getScoreTextColor, formatLeadScore } from '../../utils/leadScore';
import ContactForm from '../../components/Contacts/ContactForm';
import ActivityForm from '../../components/Activities/ActivityForm';
import AIEnrichmentPanel from '../../components/AI/AIEnrichmentPanel';
import SdrSessionsPanel from '../../components/SDR/SdrSessionsPanel';
import WhatsAppConversationPanel from '../../components/WhatsApp/WhatsAppConversationPanel';
import EmailComposeModal from '../../components/Email/EmailComposeModal';
import ScheduleMeetingModal from '../../components/Calendar/ScheduleMeetingModal';

const ContactDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  // Fetch contact details
  const { data: contact, isLoading, error } = useQuery(
    ['contact', id],
    async () => {
      const response = await api.get(`/contacts/${id}`);
      return response.data.data; // Fix: access the nested data property
    },
    {
      enabled: !!id && !!user?.id,
    }
  );

  // Delete contact mutation
  const deleteMutation = useMutation(
    () => api.delete(`/contacts/${id}`),
    {
      onSuccess: () => {
        toast.success('Contato excluído com sucesso!');
        navigate('/contacts');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao excluir contato');
      },
    }
  );

  const handleDeleteContact = () => {
    if (window.confirm(`Tem certeza que deseja excluir o contato ${contact.first_name} ${contact.last_name}?`)) {
      deleteMutation.mutate();
    }
  };

  const formatLifecycleStage = (stage) => {
    const formatMap = {
      'subscriber': 'Assinante',
      'lead': 'Lead',
      'marketing_qualified_lead': 'MQL',
      'sales_qualified_lead': 'SQL',
      'opportunity': 'Oportunidade',
      'customer': 'Cliente',
      'evangelist': 'Evangelista'
    };
    return formatMap[stage] || stage;
  };

  const formatStatus = (status) => {
    const formatMap = {
      'new': 'Novo',
      'contacted': 'Contatado',
      'qualified': 'Qualificado',
      'unqualified': 'Desqualificado',
      'follow_up': 'Follow-up',
      'meeting_scheduled': 'Reunião Agendada',
      'proposal_sent': 'Proposta Enviada',
      'negotiation': 'Negociação',
      'closed_won': 'Fechou Ganho',
      'closed_lost': 'Fechou Perdido'
    };
    return formatMap[status] || status;
  };

  const getLifecycleColor = (stage) => {
    const colorMap = {
      'subscriber': 'bg-gray-100 text-gray-800',
      'lead': 'bg-blue-100 text-blue-800',
      'marketing_qualified_lead': 'bg-yellow-100 text-yellow-800',
      'sales_qualified_lead': 'bg-orange-100 text-orange-800',
      'opportunity': 'bg-purple-100 text-purple-800',
      'customer': 'bg-green-100 text-green-800',
      'evangelist': 'bg-pink-100 text-pink-800'
    };
    return colorMap[stage] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'qualified': 'bg-green-100 text-green-800',
      'unqualified': 'bg-red-100 text-red-800',
      'follow_up': 'bg-orange-100 text-orange-800',
      'meeting_scheduled': 'bg-purple-100 text-purple-800',
      'proposal_sent': 'bg-indigo-100 text-indigo-800',
      'negotiation': 'bg-pink-100 text-pink-800',
      'closed_won': 'bg-green-100 text-green-800',
      'closed_lost': 'bg-red-100 text-red-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const formatActivityType = (type) => {
    const formatMap = {
      'call': 'Ligação',
      'email': 'Email',
      'meeting': 'Reunião',
      'note': 'Nota',
      'task': 'Tarefa',
      'whatsapp': 'WhatsApp',
      'linkedin': 'LinkedIn'
    };
    return formatMap[type] || type;
  };

  const getActivityIcon = (type) => {
    const iconMap = {
      'call': PhoneIcon,
      'email': EnvelopeIcon,
      'meeting': CalendarIcon,
      'note': ChatBubbleLeftRightIcon,
      'task': ClockIcon,
      'whatsapp': DevicePhoneMobileIcon,
      'linkedin': UserIcon
    };
    const IconComponent = iconMap[type] || ChatBubbleLeftRightIcon;
    return <IconComponent className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <div className="text-red-600 mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Erro ao carregar contato
          </h3>
          <p className="text-gray-600 mb-4">
            {error.response?.data?.message || 'Tente novamente mais tarde'}
          </p>
          <Link to="/contacts" className="text-blue-600 hover:underline">
            Voltar para lista de contatos
          </Link>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Contato não encontrado
          </h3>
          <Link to="/contacts" className="text-blue-600 hover:underline">
            Voltar para lista de contatos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link 
              to="/contacts"
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {contact.first_name} {contact.last_name}
              </h1>
              <p className="text-gray-600">
                {contact.title && `${contact.title} `}
                {contact.company && `em ${contact.company.name}`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowEditModal(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4 mr-2 inline" />
              Editar
            </button>
            <button
              onClick={handleDeleteContact}
              disabled={deleteMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4 mr-2 inline" />
              Excluir
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Contact Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Header Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-start space-x-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {contact.avatar_url ? (
                  <img
                    className="w-20 h-20 rounded-full"
                    src={contact.avatar_url}
                    alt={`${contact.first_name} ${contact.last_name}`}
                  />
                ) : (
                  <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium text-2xl">
                      {contact.first_name?.[0]}{contact.last_name?.[0]}
                    </span>
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {contact.first_name} {contact.last_name}
                  </h2>
                  {contact.lead_score && (
                    <div className="flex items-center space-x-1">
                      <StarSolidIcon className={`h-5 w-5 ${getScoreTextColor(contact.lead_score)}`} />
                      <span className={`text-sm font-medium ${getScoreTextColor(contact.lead_score)}`}>
                        {formatLeadScore(contact.lead_score)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Status Badges */}
                <div className="flex items-center space-x-2 mb-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getLifecycleColor(contact.lifecycle_stage)}`}>
                    {formatLifecycleStage(contact.lifecycle_stage)}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contact.lead_status)}`}>
                    {formatStatus(contact.lead_status)}
                  </span>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center space-x-3">
                  <a
                    href={`tel:${contact.phone}`}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    Ligar
                  </a>
                  <div className="inline-flex flex-col items-start">
                    <button
                      onClick={() => setShowEmailModal(true)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      Email
                    </button>
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="mt-1 text-xs text-gray-400 hover:text-blue-500 hover:underline pl-1"
                      >
                        abrir no meu email
                      </a>
                    )}
                  </div>
                  {contact.phone && (
                    <a
                      href={`https://wa.me/${contact.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <DevicePhoneMobileIcon className="h-4 w-4 mr-2" />
                      WhatsApp
                    </a>
                  )}
                  <button
                    onClick={() => setShowMeetingModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Agendar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Informações de Contato
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Email</p>
                    <p className="text-sm text-gray-600">{contact.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <PhoneIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Telefone</p>
                    <p className="text-sm text-gray-600">{contact.phone || 'Não informado'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Empresa</p>
                    <p className="text-sm text-gray-600">
                      {contact.company ? (
                        <Link 
                          to={`/companies/${contact.company.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {contact.company.name}
                        </Link>
                      ) : 'Não informado'}
                    </p>
                  </div>
                </div>

                {contact.birthday && (
                  <div className="flex items-center space-x-3">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Aniversário</p>
                      <p className="text-sm text-gray-600">
                        {new Date(contact.birthday).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {contact.address && (
                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Endereço</p>
                      <p className="text-sm text-gray-600">{contact.address}</p>
                    </div>
                  </div>
                )}

                {contact.lead_source && (
                  <div className="flex items-center space-x-3">
                    <div className="h-5 w-5 bg-gray-400 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">🔗</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Fonte</p>
                      <p className="text-sm text-gray-600">{contact.lead_source}</p>
                    </div>
                  </div>
                )}

                {contact.potential_value && (
                  <div className="flex items-center space-x-3">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Valor Potencial</p>
                      <p className="text-sm text-gray-600">
                        R$ {contact.potential_value.toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Criado em</p>
                    <p className="text-sm text-gray-600">
                      {new Date(contact.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            {contact.tags && contact.tags.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-2">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {contact.notes && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-900 mb-2">Observações</p>
                <p className="text-sm text-gray-600">{contact.notes}</p>
              </div>
            )}
          </div>

          {/* AI Enhancement Panel */}
          <AIEnrichmentPanel contact={contact} />

          {/* WhatsApp Conversation Thread */}
          <WhatsAppConversationPanel phone={contact?.phone || contact?.whatsapp} />
        </div>

        {/* Right Column - Activities */}
        <div className="space-y-6">
          <SdrSessionsPanel contactId={id} />

          {/* Add Activity Button */}
          <button
            onClick={() => setShowActivityModal(true)}
            className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Atividade para {contact.first_name}</span>
          </button>

          {/* Activities Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Histórico de Atividades
              </h3>
            </div>
            
            <div className="p-6">
              {contact.activities && contact.activities.length > 0 ? (
                <div className="space-y-4">
                  {contact.activities.map((activity, index) => (
                    <div key={activity.id || index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          {getActivityIcon(activity.type)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {formatActivityType(activity.type)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(activity.created_at).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {activity.subject || activity.description}
                        </p>
                        {activity.description && activity.subject && (
                          <p className="text-xs text-gray-500 mt-1">
                            {activity.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Nenhuma atividade registrada
                  </h4>
                  <p className="text-sm text-gray-600">
                    Adicione a primeira atividade para este contato.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Contact Modal */}
      {showEditModal && (
        <ContactForm
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          contact={contact}
          onSuccess={() => {
            queryClient.invalidateQueries(['contact', id]);
            setShowEditModal(false);
          }}
        />
      )}

      {/* Activity Form Modal */}
      <ActivityForm
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        contactId={id}
        companyId={contact?.company_id || null}
        onSuccess={() => {
          queryClient.invalidateQueries(['contact', id]);
          queryClient.invalidateQueries('activities');
          setShowActivityModal(false);
        }}
      />

      {/* Email Compose Modal */}
      <EmailComposeModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        contact={contact}
        onSendSuccess={() => {
          queryClient.invalidateQueries(['contact', id]);
          queryClient.invalidateQueries('activities');
        }}
      />

      {/* Schedule Meeting Modal */}
      <ScheduleMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        contact={contact}
        onScheduleSuccess={() => {
          queryClient.invalidateQueries(['contact', id]);
        }}
      />
    </div>
  );
};

export default ContactDetailPage;